import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon as ArrowLeft, GridFourIcon as GridIcon, PlusIcon as Plus, SparkleIcon as Seed, CircleNotchIcon as Spinner, WarningIcon as Warning, StackIcon as Stack } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { getWorkspace, listDispositions, listWorkspaceMembers, seedDispositions } from "@/lib/api";
import { DISPOSITIONS, SIGNAL_LABEL, STALE_CLASS, STATES, TREATMENTS, stateClass, stateLabel } from "@/lib/dispositionConstants";
import BulkDecisionPanel from "@/components/dispositions/BulkDecisionPanel";
import DispositionMatrixGrid from "@/components/dispositions/DispositionMatrixGrid";
import DispositionTable from "@/components/dispositions/DispositionTable";
import DispositionDetail from "@/components/dispositions/DispositionDetail";
import ManualDispositionForm from "@/components/dispositions/ManualDispositionForm";

/** Disposition & Treatment Matrix (Iter 39) — per-object modernization positions. */
export default function WorkspaceDispositions() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [workspace, setWorkspace] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [myRole, setMyRole] = useState(null);

  const state = params.get("state") || "";
  const treatment = params.get("treatment") || "";
  const disposition = params.get("disposition") || "";
  const kind = params.get("kind") || "";
  const signal = params.get("signal") || "";
  const stale = params.get("stale") || "";
  const q = params.get("q") || "";
  const selected = params.get("row") || "";
  const bulk = params.get("bulk") || "";

  const canEdit = user?.role === "admin" || myRole === "owner" || myRole === "editor";
  const requestSeq = useRef(0);

  const setParam = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  }, [params, setParams]);

  const load = useCallback(() => {
    const seq = ++requestSeq.current;
    setLoading(true);
    return listDispositions(id, { state, treatment, disposition, kind, signal, stale, q })
      .then((d) => { if (seq === requestSeq.current) setData(d); }) // ignore responses of superseded filters
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load dispositions."))
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, [id, state, treatment, disposition, kind, signal, stale, q]);

  useEffect(() => { getWorkspace(id).then((d) => setWorkspace(d?.workspace || null)).catch(() => {}); }, [id]);
  useEffect(() => {
    listWorkspaceMembers(id)
      .then((m) => setMyRole((m.members || []).find((x) => x.user_id === user?.id)?.role || null))
      .catch(() => setMyRole(null));
  }, [id, user]);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await seedDispositions(id);
      const staleBits = [];
      if (r.stale_flagged) staleBits.push(`${r.stale_flagged} flagged stale`);
      if (r.stale_cleared) staleBits.push(`${r.stale_cleared} stale flag(s) cleared`);
      const staleMsg = staleBits.length ? ` ${staleBits.join(", ")}.` : "";
      const toastFn = r.stale_flagged ? toast.warning : toast.success;
      toastFn(`Seeded from evidence: ${r.created} new, ${r.refreshed} refreshed, ${r.skipped_locked} validated/manual rows left untouched.${staleMsg}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seeding failed.");
    } finally {
      setSeeding(false);
    }
  };

  const byState = data?.by_state || {};
  const total = Object.values(byState).reduce((a, b) => a + b, 0);
  const staleTotal = data?.stale_total || 0;
  // the table always follows the URL's stale filter, whatever response happens to be in state
  const visibleItems = (data?.items || []).filter((d) => (stale === "true" ? d.stale : stale === "false" ? !d.stale : true));
  const bulkOpen = canEdit && (bulk === "cell" ? Boolean(treatment && disposition) : bulk === "filtered");
  const bulkSelection = bulk === "cell"
    ? { type: "matrix_cell", treatment, disposition }
    : { type: "filtered", filters: { state, treatment, disposition, kind, signal, stale, q } };
  const bulkKey = bulk === "cell" ? `cell:${treatment}:${disposition}` : `filtered:${state}:${treatment}:${disposition}:${kind}:${signal}:${stale}:${q}`;
  const sideOpen = bulkOpen || Boolean(selected);

  return (
    <div className="space-y-6" data-testid="dispositions-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to={`/workspaces/${id}`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1" data-testid="back-to-workspace">
            <ArrowLeft size={11} weight="bold" /> Back to {workspace?.name || "workspace"}
          </Link>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <GridIcon size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Disposition &amp; treatment matrix
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-2xl">
            One position per physical file or program: <em>technical treatment</em> (what happens to the code/data) kept apart from
            <em> migration disposition</em> (where it ends up). Rows start as deterministic proposals with linked evidence and become
            decisions only when a consultant validates them.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button className="ml-btn-secondary flex items-center gap-1.5" onClick={() => setShowManual((v) => !v)} data-testid="add-manual-btn">
              <Plus size={14} weight="bold" /> Add position
            </button>
            <button className="ml-btn-primary flex items-center gap-1.5" onClick={seed} disabled={seeding} data-testid="seed-dispositions-btn"
                    title="Run the deterministic rules: high-risk file → retain/refactor · decommission radar → archive/retire · orphan → retire · microservice candidate → externalize/refactor">
              {seeding ? <Spinner size={14} className="animate-spin" /> : <Seed size={14} weight="bold" />} Seed from evidence
            </button>
          </div>
        )}
      </div>

      {showManual && (
        <ManualDispositionForm workspaceId={id} onCancel={() => setShowManual(false)} onCreated={(row) => { setShowManual(false); load().then(() => setParam({ row: row.id })); }} />
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="state-chips">
        <button onClick={() => setParam({ state: "" })} className={`ml-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${!state ? "border-[var(--ml-primary)] text-[var(--ml-primary)]" : "border-[var(--ml-border)] text-[var(--ml-text-secondary)]"}`} data-testid="chip-all">
          all · {total}
        </button>
        {STATES.map((s) => (
          <button key={s} onClick={() => setParam({ state: state === s ? "" : s })} data-testid={`chip-${s}`}
                  className={`ml-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${state === s ? "ring-1 ring-[var(--ml-primary)]" : ""} ${stateClass(s)}`}>
            {stateLabel(s)} · {byState[s] ?? 0}
          </button>
        ))}
        <button onClick={() => setParam({ stale: stale === "true" ? "" : "true" })} data-testid="chip-stale"
                title={data?.stale_wording || "Stale = the driving deterministic signal no longer fires."}
                className={`ml-mono text-[10px] uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 ${stale === "true" ? "ring-1 ring-[var(--ml-primary)]" : ""} ${staleTotal ? STALE_CLASS : "border-[var(--ml-border)] text-[var(--ml-text-secondary)]"}`}>
          <Warning size={11} weight="bold" /> stale · {staleTotal}
        </button>
        <span className="ml-auto text-[11px] text-[var(--ml-text-secondary)]">
          {byState.proposed ? `${byState.proposed} proposal(s) await validation` : total ? "All rows decided" : ""}
        </span>
      </div>

      {staleTotal > 0 && stale !== "true" && (
        <div className="flex flex-wrap items-center gap-2 border border-[#ff832b]/50 bg-[#ff832b]/10 px-3 py-2 text-xs text-[#8a3800]" data-testid="stale-banner">
          <Warning size={14} weight="fill" />
          <span>
            <strong>{staleTotal} disposition{staleTotal === 1 ? "" : "s"} rest on a signal that no longer fires</strong> after the last re-seed.
            The original proposals and evidence are kept — review them before validating.
          </span>
          <button className="ml-auto underline hover:no-underline" onClick={() => setParam({ stale: "true" })} data-testid="stale-banner-show">
            Show stale only →
          </button>
        </div>
      )}

      {data && total === 0 && !loading ? (
        <div className="ml-card p-10 text-center space-y-3" data-testid="dispositions-onboarding">
          <p className="text-sm text-[var(--ml-text-secondary)] max-w-xl mx-auto">
            No positions yet. Seed proposals from the imported evidence (high-risk files, decommission radar, orphan files, microservice
            candidates) or add one by hand. Nothing is decided until you validate it.
          </p>
          {canEdit && <button className="ml-btn-primary" onClick={seed} disabled={seeding} data-testid="seed-dispositions-empty-btn">Seed from evidence</button>}
        </div>
      ) : (
        <>
          <DispositionMatrixGrid
            grid={data?.grid}
            selected={treatment && disposition ? { treatment, disposition } : null}
            onSelect={(cell) => setParam({
              treatment: cell?.treatment || "", disposition: cell?.disposition || "",
              row: "", bulk: cell && canEdit ? "cell" : "",
            })}
          />

          <div className="ml-card p-3 flex flex-wrap items-center gap-2" data-testid="disposition-filters">
            <div className="w-40"><select className="ml-input" value={treatment} onChange={(e) => setParam({ treatment: e.target.value })} data-testid="filter-treatment">
              <option value="">All treatments</option>{TREATMENTS.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
            <div className="w-40"><select className="ml-input" value={disposition} onChange={(e) => setParam({ disposition: e.target.value })} data-testid="filter-disposition">
              <option value="">All dispositions</option>{DISPOSITIONS.map((d) => <option key={d} value={d}>{d}</option>)}</select></div>
            <div className="w-40"><select className="ml-input" value={kind} onChange={(e) => setParam({ kind: e.target.value })} data-testid="filter-kind">
              <option value="">Files + programs</option><option value="physical-file">Physical files</option><option value="program">Programs</option></select></div>
            <div className="w-52"><select className="ml-input" value={signal} onChange={(e) => setParam({ signal: e.target.value })} data-testid="filter-signal">
              <option value="">All signals</option>{Object.entries(SIGNAL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div className="w-36"><select className="ml-input" value={stale} onChange={(e) => setParam({ stale: e.target.value })} data-testid="filter-stale">
              <option value="">Fresh + stale</option><option value="true">Stale only</option><option value="false">Fresh only</option></select></div>
            <div className="w-48"><input className="ml-input" placeholder="Search object…" value={q} onChange={(e) => setParam({ q: e.target.value })} data-testid="filter-q" /></div>
            {canEdit && (
              <button className="ml-btn-secondary flex items-center gap-1.5 text-xs" onClick={() => setParam({ bulk: bulk === "filtered" ? "" : "filtered", row: "" })}
                      title="Validate, reject or reopen every eligible row matching the current filters" data-testid="bulk-filtered-btn">
                <Stack size={14} weight="bold" /> Bulk decide filtered
              </button>
            )}
            <span className="ml-auto ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="dispositions-total">
              {data ? `${visibleItems.length} row${visibleItems.length === 1 ? "" : "s"}` : "—"}
            </span>
          </div>

          <div className={`grid gap-5 ${sideOpen ? "grid-cols-12" : "grid-cols-1"}`}>
            <div className={sideOpen ? "col-span-12 lg:col-span-8" : ""}>
              <div className="ml-card overflow-x-auto">
                {loading ? (
                  <div className="p-12 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]"><Spinner size={18} className="animate-spin mr-2" /> Loading…</div>
                ) : (
                  <DispositionTable items={visibleItems} selectedId={selected} onSelect={(rid) => setParam({ row: rid === selected ? "" : rid, bulk: "" })} />
                )}
              </div>
            </div>
            {bulkOpen ? (
              <div className="col-span-12 lg:col-span-4">
                <BulkDecisionPanel
                  key={bulkKey}
                  workspaceId={id}
                  selection={bulkSelection}
                  onClose={() => setParam({ bulk: "" })}
                  onDone={(res) => { if (res.affected_count > 0) { setParam({ bulk: "" }); load(); } }}
                />
              </div>
            ) : selected && (
              <div className="col-span-12 lg:col-span-4">
                <DispositionDetail workspaceId={id} dispositionId={selected} canEdit={canEdit} onClose={() => setParam({ row: "" })} onChanged={() => load()} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
